import React, { useMemo } from 'react';
import useReviewStats from '../hooks/useReviewStats';
import { Link } from 'react-router-dom';
import CardConfigurator from '../components/CardConfigurator';
import CTABanner from '../components/CTABanner';
import HotelPartners from '../components/HotelPartners';
import FuelBrandsStrip from '../components/FuelBrandsStrip';
import ComparisonTable from '../components/shared/ComparisonTable';
import {
  Building2, Users, Wallet, Wrench, Hotel, ShieldCheck,
  FileSpreadsheet, Zap, Car, Fuel, Brain, TrendingUp,
  Clock, Award, BarChart3, ArrowRight, Star,
} from 'lucide-react';
import { usePageMeta, useStructuredData, MERCHANT_OFFER_EXTRAS } from '../lib/seo';
import { getReviewsFor, buildReviewSchema } from '../data/reviews';

/**
 * BusinessLandingPage — konsolidierte B2B-Landing analog zur Silver Card.
 * Ersetzt: BusinessPage, BusinessVorteilePage, GeschaeftswagenPage,
 * BranchenPage, ReportingPage, BusinessZusatzkartePage.
 * Route: /business (+ Redirect-Kompat für alle alten Pfade)
 */
export default function BusinessLandingPage() {
  const { count: reviewCount, avgRating } = useReviewStats(180, 4.7);
  usePageMeta(
    'FuelX für Unternehmen | Fuhrpark-Vorteile & Business Tankkarte',
    'FuelX Business: Jeder Flotten-Kilometer erzeugt FuelGood-Vorteile (0,1 Punkte/km). Fuhrparks von 1 bis 5.000 Fahrzeugen — konsolidierte Abrechnung, DATEV-Export, Account-Manager. 49,90 €/Fahrzeug/Jahr.',
    { canonical: 'https://fuelxcard.com/business' },
  );

  const productSchema = useMemo(() => ({
    '@type': 'Product',
    name: 'FuelX Gold Card — Business Flotten-Management',
    description: 'Business-Card für Firmenwagen und Flotten. KI-optimierte Kosten, konsolidierte Abrechnung, DATEV-Export, Team-Dashboard, dedizierter Account-Manager.',
    image: 'https://fuelxcard.com/gold-card-front.webp',
    brand: { '@type': 'Brand', name: 'FuelX' },
    sku: 'FUELX-GOLD-BUSINESS',
    category: 'Fleet Management Card',
    offers: {
      '@type': 'AggregateOffer',
      priceCurrency: 'EUR',
      lowPrice: '49.90',
      highPrice: '119.90',
      priceValidUntil: '2026-12-31',
      availability: 'https://schema.org/InStock',
      url: 'https://fuelxcard.com/business',
      ...MERCHANT_OFFER_EXTRAS,
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: String(avgRating),
      reviewCount: String(reviewCount),
      bestRating: '5', worstRating: '1',
    },
    review: buildReviewSchema('Gold Card', 6),
  }), [reviewCount, avgRating]);
  useStructuredData(productSchema, 'business-product');

  const reviews = getReviewsFor('business', 3);

  return (
    <div className="min-h-screen bg-white pt-[92px]" data-testid="business-landing-page">
      {/* HERO */}
      <section className="pt-10 pb-4 text-center px-6">
        <nav className="text-[11px] text-stone-400 mb-4 tracking-wider uppercase" data-testid="business-breadcrumb">
          <Link to="/" className="hover:text-stone-900 transition-colors">FuelX</Link>
          <span className="mx-2">/</span>
          <span className="text-stone-600">Business</span>
        </nav>
        <p className="text-xs font-medium uppercase tracking-[0.2em] text-stone-400 mb-3">Für Unternehmen · Flotten · Fuhrparks</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-[-0.03em] text-stone-900 mb-5" data-testid="business-heading">
          Deine Flotte fährt. Deine Vorteile wachsen.
        </h1>
        <p className="text-base sm:text-lg text-stone-500 max-w-2xl mx-auto">
          Jeder gefahrene Kilometer deiner Fahrzeuge erzeugt FuelGood-Vorteile — nutzbar über qualifizierende Buchungen und Mobilitätsservices für den Fuhrpark. Ein Dashboard, konsolidierte Abrechnung, Guthaben skaliert mit deiner Flotte.
        </p>
      </section>

      {/* Konfigurator im Business-Modus */}
      <CardConfigurator initialMode="business" lockMode showHeading={false} hideAvatar />
      <FuelBrandsStrip testId="business-fuel-brands" />

      {/* ═══ CORE-VORTEILE ═══ */}
      <section className="py-20 sm:py-28 border-b border-stone-100">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid sm:grid-cols-4 gap-6">
            {[
              { icon: Users, title: 'Bis 5.000 Fahrzeuge', desc: 'Von 3-Personen-Handwerksbetrieb bis Mittelstand.' },
              { icon: BarChart3, title: 'Team-Dashboard', desc: 'Alle Fahrer, alle Kosten, ein Ort. Live.' },
              { icon: FileSpreadsheet, title: 'DATEV-Export', desc: 'Steuerberater-fertig — 1-Klick-Buchungsstapel.' },
              { icon: Building2, title: 'Account-Manager', desc: 'Ab 20 Fahrzeugen: persönlicher B2B-Kontakt.' },
            ].map((b, i) => {
              const Icon = b.icon;
              return (
                <div key={b.title} className="text-stone-700" data-testid={`business-benefit-${i}`}>
                  <div className="w-10 h-10 rounded-xl bg-stone-900 text-white flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5" strokeWidth={1.8} />
                  </div>
                  <h3 className="text-base font-medium text-stone-900 mb-1">{b.title}</h3>
                  <p className="text-sm text-stone-500 leading-relaxed">{b.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ DAS ROI-VERSPRECHEN ═══ */}
      <section className="py-24 sm:py-32 bg-stone-950 text-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <p className="text-xs font-medium uppercase tracking-[0.28em] text-emerald-400 mb-4">Das ROI-Versprechen</p>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-[-0.025em] leading-[1.05] mb-8">
            Bei 10 Fahrzeugen sparst du <span className="text-emerald-400">≈ 12 Stunden Verwaltung / Monat</span> und ≈ 3.400 €/Jahr.
          </h2>
          <p className="text-base sm:text-lg text-stone-400 leading-relaxed max-w-3xl mb-12">
            Nicht das Cashback ist das Business-Argument — es ist die Zeit deines Fuhrpark­verantwortlichen und die Konsolidierung von 5-8 Einzelverträgen in ein Dashboard.
          </p>
          <div className="grid sm:grid-cols-3 gap-6" data-testid="business-roi-tiles">
            {[
              { value: '≈ 12h', label: 'Verwaltungszeit pro Monat gespart', sub: 'Rechnungen · Werkstattkoordination · Steuerbescheinigungen' },
              { value: '≈ 3.400 €', label: 'Ø jährliche Kostenersparnis · 10 Fzg.', sub: 'KI-Vertragsprüfung · konsolidierte Werkstattpreise · Guthaben' },
              { value: '≈ 4-8 Wochen', label: 'ROI-Break-Even ab Vertragsstart', sub: 'Amortisation über die Guthaben-Aufladungen' },
            ].map((r) => (
              <div key={r.label} className="border border-white/10 rounded-2xl p-6 bg-white/[0.02]">
                <p className="text-4xl font-display tracking-tight text-emerald-400 tabular-nums">{r.value}</p>
                <p className="text-sm font-medium text-white mt-3 leading-tight">{r.label}</p>
                <p className="text-xs text-stone-500 mt-1.5 leading-relaxed">{r.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ MIT vs OHNE — Direktvergleich Fuhrpark ═══ */}
      <ComparisonTable variant="business" testId="business-comparison-table" />

      {/* ═══ ALLE VORTEILE ═══ */}
      <section className="py-24 sm:py-32">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mb-14">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-stone-400 mb-4">Alle Vorteile für deine Flotte</p>
            <h2 className="font-display text-3xl sm:text-4xl tracking-[-0.025em] text-stone-900 leading-[1.05]">
              Ein Vertrag. Ein Dashboard. Alles was der Fuhrpark braucht.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
            {[
              { icon: Wallet, title: 'FuelGood-Guthaben je Fahrzeug', text: '0,075 € pro km und Fahrzeug — automatisch auf die Prepaid-Tankkarte. Für Diesel & Elektro (Ladesäulen der Dienstleister).' },
              { icon: FileSpreadsheet, title: 'Monatliche Sammelabrechnung', text: 'Eine PDF-Rechnung pro Monat statt 40-100 Einzelbelege. DATEV-Buchungsstapel per 1-Klick.' },
              { icon: Wrench, title: 'Werkstatt-Festpreis + Terminmanagement', text: 'FuelX bucht TÜV, Öl, Reifen bei geprüften Dienstleister-Werkstätten. Rechnung geht aus dem Guthaben.' },
              { icon: Brain, title: 'KI-Vertragsprüfung', text: 'KI prüft Versicherung, Leasingverträge, Reifen- und Wartungsverträge — meldet bessere Konditionen automatisch.' },
              { icon: Users, title: 'Fahrer-Verwaltung', text: 'Jeder Fahrer bekommt eine Zusatzkarte. Alle Guthaben fließen in dein Haupt-Konto. Fahrer-Aktivität transparent.' },
              { icon: Hotel, title: 'Geschäftsreisen', text: '1,6 Mio Hotels weltweit, konsolidierte Reisekosten-Abrechnung, FuelGood Guthaben pro qualifizierender Buchung.' },
              { icon: ShieldCheck, title: 'DSGVO-konform · Server in DE', text: 'Alle Daten in Frankfurt. Auftragsverarbeitungsvertrag auf Anfrage. Datenschutzbeauftragter beratend.' },
              { icon: Building2, title: 'Dedizierter Account-Manager', text: 'Ab 20 Fahrzeugen: persönlicher B2B-Ansprechpartner. E-Mail + Telefon-Direktleitung.' },
            ].map((v) => {
              const Icon = v.icon;
              return (
                <div key={v.title} className="flex items-start gap-4" data-testid={`business-value-${v.title.replace(/[^a-z]/gi, '').toLowerCase().slice(0, 20)}`}>
                  <div className="flex-shrink-0 w-11 h-11 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-emerald-700" strokeWidth={1.8} />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base font-medium text-stone-900 leading-tight">{v.title}</h3>
                    <p className="text-sm text-stone-500 leading-relaxed mt-1.5">{v.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ PRICING BUSINESS ═══ */}
      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-stone-400 mb-4">Preisstaffelung · Business</p>
          <h2 className="font-display text-3xl sm:text-4xl tracking-[-0.025em] text-stone-900 mb-4">Skaliert mit deiner Flotte.</h2>
          <p className="text-base text-stone-500 mb-10 leading-relaxed">
            Zahle pro Fahrzeug und Jahr. Klare Staffel, keine versteckten Kosten.
          </p>
          <div className="space-y-3">
            {[
              { range: '1–10 Fahrzeuge · Handwerk & Kleinbetrieb', price: '49,90 €/Fzg·Jahr', credit: '0,075 €/km FuelGood-Guthaben' },
              { range: '11–100 Fahrzeuge · Mittelstand', price: '39,90 €/Fzg·Jahr', credit: 'Account-Manager ab 20 Fzg.' },
              { range: '101–5.000 Fahrzeuge · Konzern', price: '49,90 €/Fzg·Jahr', credit: 'Angebot auf Anfrage · Enterprise-SLA' },
            ].map((t) => (
              <div key={t.range} className="flex items-center justify-between bg-white rounded-2xl border border-stone-200 p-6" data-testid="business-tier">
                <div>
                  <p className="font-medium text-stone-900">{t.range}</p>
                  <p className="text-sm text-stone-400 mt-0.5">{t.credit}</p>
                </div>
                <span className="text-2xl font-medium tracking-tight text-stone-900">{t.price}</span>
              </div>
            ))}
          </div>
          <div className="mt-6 flex items-start gap-3 rounded-2xl bg-emerald-50 border border-emerald-100 p-4 text-[13px] text-emerald-800 leading-relaxed">
            <Zap className="w-4 h-4 flex-shrink-0 mt-0.5" strokeWidth={2} />
            <span><strong className="font-medium">Test-Phase 30 Tage risikofrei.</strong> Bei Kündigung innerhalb der ersten 30 Tage: volle Erstattung der Card-Gebühren. Das eingelöste Guthaben bleibt bei dir.</span>
          </div>
        </div>
      </section>

      {/* ═══ FÜR WEN? ═══ */}
      <section className="py-24 sm:py-32">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-stone-400 mb-4 text-center">Passt zu dir wenn du…</p>
          <h2 className="font-display text-3xl sm:text-4xl tracking-[-0.025em] text-stone-900 mb-16 text-center">Für diese Branchen macht FuelX Sinn</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { title: 'Handwerksbetrieb', desc: '3–15 Servicefahrzeuge, viele Kundentermine. Werkstattkoordination + Reisekosten in einem System.' },
              { title: 'Handelsvertretung / Außendienst', desc: '5–50 Firmenwagen, viele Übernachtungen. Hotel-Guthaben sammelt sich schnell auf 15.000–50.000 €/Jahr.' },
              { title: 'Logistik-Kleinflotten', desc: 'Transporter, Sprinter, Kastenwagen. Konsolidierte Tank- und Werkstattabrechnung + KI-Reifenverträge.' },
              { title: 'Immobilien-Verwaltung', desc: '10–100 Objektverwalter mit Firmenwagen. DATEV-Export der Fahrzeugkosten pro Objekt.' },
              { title: 'Pflegedienst & Sozialarbeit', desc: 'Viele Kleinwagen, viele km/Jahr. Guthaben deckt einen großen Teil der Sprit- und Servicekosten ab.' },
              { title: 'IT-Systemhäuser', desc: 'Berater & Techniker vor Ort beim Kunden. Hotel-Guthaben + Vertragsprüfung amortisiert die Card sofort.' },
            ].map((p) => (
              <div key={p.title} className="border-t-2 border-stone-900 pt-6" data-testid="business-persona">
                <h3 className="text-lg font-medium text-stone-900 mb-2">{p.title}</h3>
                <p className="text-sm text-stone-500 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ REVIEWS (aus zentraler Quelle) ═══ */}
      {reviews.length > 0 && (
        <section className="py-24 sm:py-32 bg-white" data-testid="business-reviews">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-stone-400 mb-4">Business-Kundenstimmen</p>
            <h2 className="font-display text-3xl sm:text-4xl tracking-[-0.025em] text-stone-900 mb-14 leading-[1.05]">Was Fuhrpark-Verantwortliche sagen.</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {reviews.map((r) => (
                <article key={r.id} className="bg-white rounded-3xl border border-stone-100 p-6" data-testid={`business-review-${r.id}`}>
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: r.rating }).map((_, k) => <Star key={k} className="w-3.5 h-3.5 text-amber-500" fill="currentColor" strokeWidth={0} />)}
                  </div>
                  <p className="text-sm text-stone-700 leading-relaxed italic mb-4">&bdquo;{r.short || r.text}&ldquo;</p>
                  <div className="flex items-center gap-3 pt-4 border-t border-stone-100">
                    <div>
                      <p className="text-sm font-medium text-stone-900">{r.name}, {r.age}</p>
                      <p className="text-[11px] text-stone-400">{r.role} · {r.city}</p>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      <HotelPartners compact />

      <CTABanner
        headline="Bereit für FuelX Business?"
        text="Ab 49,90 €/Fahrzeug·Jahr. Test-Phase 30 Tage risikofrei. ROI ab 4-8 Wochen."
      />
    </div>
  );
}
